import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ConfirmModal from '../components/Shared/ConfirmModal';
import SearchableClientSelect from '../components/Shared/SearchableClientSelect';
import { getYear, parseISO } from 'date-fns';

const Tasks = () => {
  const { tasks, clients, agencies, addTask, removeTask, archiveTask } = useAppContext();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('Open');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [search, setSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [newTask, setNewTask] = useState({ clientId: '', title: '', priority: 'Medium', loggedHours: 0 });

  const filteredTasks = tasks
    .filter(task => {
      // Robust case-insensitive status matching
      const tStatus = (task.status || 'Open').toString().trim().toLowerCase();
      let matchesTab = false;
      if (activeTab === 'Open') matchesTab = tStatus === 'open';
      else if (activeTab === 'Closed') matchesTab = tStatus === 'closed';
      else if (activeTab === 'Archived') matchesTab = tStatus === 'archived';

      // Robust date extraction to prevent NaN crashes
      let taskYear;
      try {
        const parsed = parseISO(task.date || new Date().toISOString());
        taskYear = isNaN(parsed) ? new Date().getFullYear().toString() : getYear(parsed).toString();
      } catch (e) {
        taskYear = new Date().getFullYear().toString();
      }
      
      const matchesYear = (activeTab === 'Open') || (taskYear === selectedYear);
      
      // Robust string matching to prevent unexpected undefined access
      const matchesSearch = (task.title || '').toString().toLowerCase().includes(search.toLowerCase());
      const client = clients.find(c => c.id === task.clientId);
      const matchesClient = (client?.name || '').toString().toLowerCase().includes(clientSearch.toLowerCase());

      return matchesTab && matchesYear && matchesSearch && matchesClient;
    })
    .sort((a, b) => {
      // Failsafe sorting prevents NaN - NaN exceptions
      const dateA = new Date(a.last_updated || a.date || new Date()).getTime();
      const dateB = new Date(b.last_updated || b.date || new Date()).getTime();
      return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
    });

  const handleCreate = (status = 'Open') => {
    if (!newTask.clientId) return alert("Select a client.");
    addTask({ ...newTask, status });
    setIsModalOpen(false);
    setNewTask({ clientId: '', title: '', priority: 'Medium', loggedHours: 0 });
  };

  return (
    <div className="space-y-6 sm:space-y-8 pb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 sm:gap-4">
        <div>
          <p className="text-teal-500 font-bold text-sm uppercase tracking-widest mb-1">Service Queue</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Task Management</h1>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto bg-teal-600 hover:bg-teal-500 text-white px-5 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-black shadow-lg shadow-teal-900/20 text-sm">
          <SafeIcon icon={FiIcons.FiPlus} /> New Task
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
        <div className="flex flex-wrap bg-slate-900 p-1 rounded-2xl border border-slate-800">
          {['Open', 'Closed', 'Archived'].map(tab => {
            const count = tasks.filter(t => (t.status || 'Open').toString().trim().toLowerCase() === tab.toLowerCase()).length;
            return (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab)} 
                className={`flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === tab ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-white hover:bg-slate-800/50'}`}
              >
                {tab}
                <span className={`flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[10px] font-black ${activeTab === tab ? 'bg-teal-800 text-teal-100' : 'bg-slate-800 text-slate-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative flex-1">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search tasks..." className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-12 pr-6 py-3.5 text-sm text-slate-200 outline-none focus:ring-2 focus:ring-teal-500/50" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="relative sm:w-64">
            <SafeIcon icon={FiIcons.FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Filter by client..." className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-12 pr-6 py-3.5 text-sm text-slate-200 outline-none focus:ring-2 focus:ring-teal-500/50" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left min-w-[800px]">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-800/30">
                <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Priority</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Subject</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Client</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Last Updated</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Logged</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredTasks.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-8 py-12 text-center text-slate-500 font-bold">No tasks found.</td>
                </tr>
              )}
              {filteredTasks.map(task => {
                const client = clients.find(c => c.id === task.clientId);
                return (
                  <tr key={task.id} onClick={() => navigate(`/tasks/${task.id}`)} className="hover:bg-slate-800/50 cursor-pointer transition-colors group">
                    <td className="px-8 py-6">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase border ${task.priority === 'High' ? 'bg-red-500/10 text-red-500 border-red-500/20' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                        {task.priority || 'Medium'}
                      </span>
                    </td>
                    <td className="px-8 py-6 min-w-[250px]">
                      <p className="font-bold text-slate-200 group-hover:text-teal-400 transition-colors text-sm line-clamp-1 uppercase">{task.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest">#{task.id.slice(-6).toUpperCase()}</p>
                        {client?.agencyId && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-black uppercase flex items-center gap-1 tracking-wider">
                             <SafeIcon icon={FiIcons.FiBriefcase} className="text-[8px]" />
                             {agencies.find(a => a.id === client.agencyId)?.name || 'Agency'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-slate-400 font-bold text-sm uppercase">{client?.name || 'Unknown'}</span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <p className="font-bold text-slate-300 text-sm whitespace-nowrap">
                        {new Date(task.last_updated || task.date || new Date()).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <p className="font-mono font-black text-teal-400 text-base">{(task.loggedHours || 0).toFixed(1)}h</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)} >
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-white mb-8">New Support Task</h2>
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Client Account</label>
                <SearchableClientSelect clients={clients} value={newTask.clientId} onChange={(val) => setNewTask({ ...newTask, clientId: val })} placeholder="Search clients..." />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Task Subject</label>
                <input autoFocus className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm" value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} placeholder="e.g. Homepage Update" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Priority</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm appearance-none" value={newTask.priority} onChange={e => setNewTask({ ...newTask, priority: e.target.value })}>
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Hours</label>
                  <input type="number" step="0.5" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm" value={newTask.loggedHours} onChange={e => setNewTask({ ...newTask, loggedHours: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-6">
                <button type="button" onClick={() => handleCreate('Open')} className="w-full px-4 py-3.5 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 shadow-lg shadow-teal-900/40 text-sm uppercase">Create Task</button>
                <button type="button" onClick={() => setIsModalOpen(false)} className="w-full px-6 py-2 text-slate-500 hover:text-white font-bold text-sm uppercase">Discard</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={deleteConfirm.show} onClose={() => setDeleteConfirm({ show: false, id: null })} onConfirm={() => removeTask(deleteConfirm.id)} title="Delete Task?" message="Permanently delete this task and all time records?" />
    </div>
  );
};

export default Tasks;